import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  model,
  OnInit, signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {NgOptimizedImage} from "@angular/common";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {TextAreaComponent, TextAreaOptions} from "@shared/components/text-area/text-area.component";
import {Coding} from '@core/classes/other/coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {HttpHeaders} from '@angular/common/http';
import {assets} from '@core/constants';
import {IllnessSearchComponent} from '@shared/components/dropdown/search/illness-search/illness-search.component';
import {CurrentIllness} from '@core/classes/tables/current-illness';
import {ResponseSingle} from '@core/classes/other/response';
import {checkDate} from '@core/helpers';
import moment from 'moment';

@Component({
  selector: 'ncp-add-problem-diagnosis',
    imports: [
        ButtonComponent,
        DateComponent,
        DropdownComponent,
        ModalComponent,
        NgOptimizedImage,
        ReactiveFormsModule
    ],
  templateUrl: './add-problem-diagnosis.component.html',
  styleUrl: './add-problem-diagnosis.component.scss',
})
export class AddProblemDiagnosisComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('pdFrm') protected problemFormEl?: NgForm;
  @ViewChild('addProblemButton') public addProblemButton!: ButtonComponent;

  @ViewChild( 'problemDesc') public problemDesc!: DropdownComponent<Coding>;
  @ViewChild('problemDate') public problemDate!: DateComponent;

  @ViewChild('problemTemplate') public problemTemplate!: TemplateRef<Coding>;

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
    onset_date: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let codingId = (control as FormGroup).controls['coding_id'];
      let onSetDate = (control as FormGroup).controls['onset_date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();


      if(codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({pd_codingIdInvalid: true});
        errors['pd_codingIdInvalid'] = true;
      }

      if((!checkDate(onSetDate.value).isValid())) {
        onSetDate.setErrors({pd_problemDateInvalid: true});
        errors['pd_problemDateInvalid'] = true;
      } else {
        if(checkDate(onSetDate.value).isBefore(patientDOB)) {
          onSetDate.setErrors({pd_problemDateAfterDOB: true});
          errors['pd_problemDateAfterDOB'] = true;
        } else if(checkDate(onSetDate.value).isSameOrAfter(today)) {
          onSetDate.setErrors({pd_problemDateBeforeToday: true});
          errors['pd_problemDateBeforeToday'] = true;
        } else {
          onSetDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  getFormControl = (name: string) => {
    return this.form.get(name) as FormControl;
  }

  public btnPDOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'pdBtn',
      tooltip: 'add_problem',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      type: ModalTypes.form,
      form: FormComponent,
      title: 'add_problem',
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected problemDescDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'problemDescription',
      name: 'problemDescription',
      label: {
        id: "problem_desc_label",
        text: 'problem_description',
      },
      api: {
        description: {
          path: '/get-all_codings',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.5',
            description: ''
          }
        },
        code: {
          path: '/get-icd10',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: ''
          }
        }
      },
      required: true,
      searchComponent: IllnessSearchComponent,
      formControl: this.form.get('coding_id') as FormControl
    })
  );

  protected onSetDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'piOnSetDate',
      label: {
        text: 'on_set',
      },
      name: 'piOnSetDate',
      initialValue: '',
      required: true,
      formControl: this.form.get('onset_date') as FormControl,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  ngOnInit() {
    this.btnPDOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      let problemOptions = this.problemDescDDOptions();
      problemOptions.itemTemplate = this.problemTemplate;
      this.problemDescDDOptions.set(problemOptions);
      this.problemDesc.detectChanges();
      this.addProblemButton.blur();
      this.mod.open();
    };
    //this.onSetDateOptions().formControl = (this.form.get('onset_date') as FormControl);
    this.modOptions().onSave = this.onSave;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.problemDesc.clearSelection();
    this.problemDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  onSave = () => {
    this.problemFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      onset_date: (this.form.get('onset_date') as FormControl).value,
      coding_id: this.problemDescDDOptions().value!.coding_id,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/illness',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<CurrentIllness>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data;
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
